'use client';

import { useFormatter, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { POSITIONS_PATH } from '@/lib/components/Header/routes';
import { pnlToneClass } from '@/lib/utils/calculatePnl';
import { formatMasked, formatMaskedMoney } from '@/lib/utils/formatAmount';
import { SummaryRow } from './SummaryRow';

interface PredictionMarketSummaryProps {
  openPositions: number;
  openOrders: number;
  positionValue: number;
  pnl: number;
  hidden: boolean;
  renderAccountLink: (href: string, label: ReactNode) => ReactNode;
}

const ORDERS_HREF = '/account?tab=deals&dealsTab=activeOrders';
const USD_FORMAT = { prefix: '$', decimals: 2 };

export function PredictionMarketSummary({
  openPositions,
  openOrders,
  positionValue,
  pnl,
  hidden,
  renderAccountLink,
}: PredictionMarketSummaryProps) {
  const t = useTranslations('balancePopover');
  const format = useFormatter();

  return (
    <div className="flex flex-col gap-1">
      {renderAccountLink(
        POSITIONS_PATH,
        <SummaryRow
          label={t('openPosition')}
          value={formatMasked(openPositions, hidden, format.number)}
          interactive
        />,
      )}
      {renderAccountLink(
        ORDERS_HREF,
        <SummaryRow
          label={t('openOrders')}
          value={formatMasked(openOrders, hidden, format.number)}
          interactive
        />,
      )}
      <SummaryRow
        label={t('positionValue')}
        value={formatMaskedMoney(Math.abs(positionValue), hidden, USD_FORMAT)}
      />
      <SummaryRow
        label={t('unrealizedPnl')}
        value={formatMaskedMoney(pnl, hidden, USD_FORMAT)}
        valueClassName={pnlToneClass(pnl)}
      />
    </div>
  );
}
